import { useCallback, useEffect, useRef, useState } from "react";
import { animationMotionTimings, animationTransitionTimings, type AnimationMotionTiming } from "@/document/animation/applyAnimation";
import type { AnimationGraph, MotionAnimationNode } from "@/document/model/animation";
import type { CurvaDocument } from "@/document/model/document";
import { shouldReleaseTimelineFocusAfterPointerScrub } from "./animationFocus";

const RULER_HEIGHT = 20;
const LOOP_HEIGHT = 7;
const ROW_SLOT = 28;
const BAR_HEIGHT = 20;
const BAR_TOP = (ROW_SLOT - BAR_HEIGHT) / 2;
const TRACK_LABEL_WIDTH = 148;
const RESIZE_PX = 6;
const MARKER_LABEL_WIDTH = 148;
const MARKER_LINE_WIDTH = 12;
const MIN_DURATION_MS = 10;
const MIN_LOOP_DURATION_MS = 10;
const MIN_PIXELS_PER_MS = 0.01;
const MAX_PIXELS_PER_MS = 5;
const RIGHT_OVERSCROLL_PX = 300;
const EDGE_SNAP_PX = 8;
const TICK_STEPS_MS = [10, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

function niceTickStep(pixelsPerMs: number): number {
  for (const step of TICK_STEPS_MS) {
    if (step * pixelsPerMs >= 50) return step;
  }
  return 10000;
}

function formatMs(ms: number): string {
  if (ms === 0) return "0";
  if (ms >= 1000 && ms % 1000 === 0) return `${ms / 1000}s`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

type DragKind =
  | { kind: "none" }
  | { kind: "scrub"; startX: number; startTimeMs: number; pxPerMs: number; releaseFocusOnEnd: boolean }
  | { kind: "moveNode"; nodeId: string; startX: number; startStartMs: number; startDurationMs: number; offsetBaseMs: number; pxPerMs: number }
  | { kind: "resizeNode"; nodeId: string; startX: number; startDurationMs: number; pxPerMs: number }
  | { kind: "moveLoop"; startX: number; startRegion: AnimationLoopRegion; pxPerMs: number }
  | { kind: "resizeLoop"; edge: "start" | "end"; startX: number; startRegion: AnimationLoopRegion; pxPerMs: number }
  | { kind: "pan"; startX: number; startY: number; startPanMs: number; startVerticalPanPx: number; pxPerMs: number };

interface DragPreview {
  nodeId: string;
  offsetMs?: number;
  durationMs?: number;
}

interface TimelineBar {
  timing: AnimationMotionTiming;
  rootNodeId: string;
  lane: number;
  startMs: number;
  durationMs: number;
  effectiveDurationMs: number;
  stutterTailMs: number;
  endMs: number;
  packEndMs: number;
  offsetBaseMs: number;
  hasOffset: boolean;
  isMarker: boolean;
  originalIndex: number;
  rootDepth: number;
}

interface TimelineTrack {
  rootNodeId: string;
  name: string;
  laneStart: number;
  laneCount: number;
}

interface TimelineMotionTiming {
  timing: AnimationMotionTiming;
  rootNodeId: string;
  offsetBaseMs: number;
  rootDepth: number;
}

export interface AnimationLoopRegion {
  startMs: number;
  endMs: number;
}

export interface AnimationTimelineViewport {
  pixelsPerMs: number;
  panOffsetMs: number;
  verticalPanPx: number;
}

export const DEFAULT_ANIMATION_TIMELINE_VIEWPORT: AnimationTimelineViewport = {
  pixelsPerMs: 0.2,
  panOffsetMs: 0,
  verticalPanPx: 0,
};

export const DEFAULT_ANIMATION_TIMELINE_VIEWPORT_KEY = "__all__";

export function animationTimelineViewportKey(rootNodeId: string | undefined): string {
  return rootNodeId ?? DEFAULT_ANIMATION_TIMELINE_VIEWPORT_KEY;
}

export function animationTimelineViewportsWith(
  viewports: Record<string, AnimationTimelineViewport>,
  rootNodeId: string | undefined,
  viewport: AnimationTimelineViewport,
): Record<string, AnimationTimelineViewport> {
  return {
    ...viewports,
    [animationTimelineViewportKey(rootNodeId)]: viewport,
  };
}

export interface AnimationTimelineProps {
  animationGraph: AnimationGraph;
  document: CurvaDocument;
  rootNodeId?: string;
  timeMs: number;
  durationMs: number;
  playing: boolean;
  selectedAnimationNodeIds: string[];
  loopRegion?: AnimationLoopRegion;
  viewport: AnimationTimelineViewport;
  onTimeChange: (ms: number) => void;
  onSelectAnimationNodes: (ids: string[], primaryId: string | undefined, options?: { switchRoot?: boolean }) => void;
  onUpdateMotionNode: (nodeId: string, changes: { durationMs?: number; offsetMs?: number; name?: string }) => void;
  onLoopRegionChange: (region: AnimationLoopRegion | undefined) => void;
  onViewportChange: (viewport: AnimationTimelineViewport) => void;
}

export function AnimationTimeline({
  animationGraph,
  document: curvaDocument,
  rootNodeId,
  timeMs,
  durationMs,
  playing,
  selectedAnimationNodeIds,
  loopRegion,
  viewport,
  onTimeChange,
  onSelectAnimationNodes,
  onUpdateMotionNode,
  onLoopRegionChange,
  onViewportChange,
}: AnimationTimelineProps) {
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [editingName, setEditingName] = useState<{ nodeId: string; value: string } | null>(null);
  const dragRef = useRef<DragKind>({ kind: "none" });
  const timelineRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const previousPlayingRef = useRef(false);
  const viewportRef = useRef(viewport);
  const pixelsPerMs = viewport.pixelsPerMs;
  const panOffsetMs = viewport.panOffsetMs;
  const verticalPanPx = viewport.verticalPanPx;
  viewportRef.current = viewport;

  const timelineTimings = timelineMotionTimings(animationGraph, rootNodeId, curvaDocument);
  const packedTimeline = packTimelineTracks(timelineTimings.map((item, originalIndex) => {
    const timing = item.timing;
    const startMs = effectiveStart(timing.motion, timing.startMs);
    const durationMs = effectiveDuration(timing.motion);
    const stutterTailMs = timing.stutterTailMs;
    const effectiveDurationMs = durationMs + stutterTailMs;
    const effectiveOffsetMs = startMs - item.offsetBaseMs;
    return {
      timing,
      rootNodeId: item.rootNodeId,
      lane: 0,
      startMs,
      durationMs,
      effectiveDurationMs,
      stutterTailMs,
      endMs: startMs + effectiveDurationMs,
      packEndMs: startMs + effectiveDurationMs,
      offsetBaseMs: item.offsetBaseMs,
      hasOffset: Math.round(effectiveOffsetMs) !== 0,
      isMarker: effectiveDurationMs <= 0,
      originalIndex,
      rootDepth: item.rootDepth,
    };
  }), animationGraph);
  const motionBars = packedTimeline.bars;
  const timelineTracks = packedTimeline.tracks;
  const showTrackLabels = timelineTracks.length > 1;
  const laneCount = motionBars.reduce((count, bar) => Math.max(count, bar.lane + 1), 0);
  const effectiveLoopRegion = normalizeLoopRegion(loopRegion, durationMs);
  const timelineContentEndMs = Math.max(
    durationMs,
    effectiveLoopRegion?.endMs ?? 0,
    ...motionBars.map(bar => bar.isMarker ? bar.startMs + MARKER_LABEL_WIDTH / pixelsPerMs : bar.endMs),
  );

  function effectiveStart(node: MotionAnimationNode, startMs: number): number {
    if (dragPreview?.nodeId === node.id && dragPreview.offsetMs !== undefined) {
      return dragPreview.offsetMs;
    }
    return startMs;
  }

  function effectiveDuration(node: MotionAnimationNode): number {
    if (dragPreview?.nodeId === node.id && dragPreview.durationMs !== undefined) {
      return dragPreview.durationMs;
    }
    return node.durationMs;
  }

  const timeToX = useCallback(
    (ms: number) => (ms - panOffsetMs) * pixelsPerMs,
    [panOffsetMs, pixelsPerMs],
  );

  const xToTime = useCallback(
    (x: number) => x / pixelsPerMs + panOffsetMs,
    [panOffsetMs, pixelsPerMs],
  );

  function maxVerticalPanPx(): number {
    const viewportHeight = Math.max(0, (timelineRef.current?.clientHeight ?? 0) - RULER_HEIGHT);
    return Math.max(0, laneCount * ROW_SLOT - viewportHeight);
  }

  function clampVerticalPanPx(value: number): number {
    return Math.max(0, Math.min(value, maxVerticalPanPx()));
  }

  function maxHorizontalPanMs(pxPerMs = pixelsPerMs): number {
    const viewportWidth = timelineRef.current?.clientWidth ?? 0;
    const visibleContentWidth = Math.max(0, viewportWidth - RIGHT_OVERSCROLL_PX);
    return Math.max(0, timelineContentEndMs - visibleContentWidth / pxPerMs);
  }

  function clampHorizontalPanMs(value: number, pxPerMs = pixelsPerMs): number {
    return Math.max(0, Math.min(value, maxHorizontalPanMs(pxPerMs)));
  }

  function updateViewport(update: (current: AnimationTimelineViewport) => AnimationTimelineViewport): void {
    const next = update(viewportRef.current);
    viewportRef.current = next;
    onViewportChange(next);
  }

  function setPanOffsetMs(next: number | ((current: number) => number)): void {
    updateViewport(current => ({
      ...current,
      panOffsetMs: typeof next === "function" ? next(current.panOffsetMs) : next,
    }));
  }

  function setVerticalPanPx(next: number | ((current: number) => number)): void {
    updateViewport(current => ({
      ...current,
      verticalPanPx: typeof next === "function" ? next(current.verticalPanPx) : next,
    }));
  }

  function revealTime(ms: number): void {
    const width = timelineRef.current?.clientWidth ?? 0;
    if (width <= 0) return;
    const margin = Math.min(80, Math.max(24, width * 0.12));
    const x = timeToX(ms);
    if (x < margin) {
      setPanOffsetMs(clampHorizontalPanMs(ms - margin / pixelsPerMs));
    } else if (x > width - margin) {
      setPanOffsetMs(clampHorizontalPanMs(ms - (width - margin) / pixelsPerMs));
    }
  }

  function selectedLoopBounds(): AnimationLoopRegion | undefined {
    const selectedBars = motionBars.filter(bar => selectedAnimationNodeIds.includes(bar.timing.motion.id));
    if (selectedBars.length === 0) {
      return durationMs > 0 ? { startMs: 0, endMs: durationMs } : undefined;
    }
    const startMs = Math.min(...selectedBars.map(bar => bar.startMs));
    const endMs = Math.max(...selectedBars.map(bar => bar.endMs));
    return normalizeLoopRegion({ startMs, endMs }, durationMs);
  }

  function snappedMoveStartMs(nodeId: string, startMs: number, barDurationMs: number, pxPerMs = pixelsPerMs): number {
    const clampedStart = Math.max(0, startMs);
    const snappedStart = snapTimeToBarEdges(clampedStart, nodeId, pxPerMs);
    const snappedEnd = snapTimeToBarEdges(clampedStart + barDurationMs, nodeId, pxPerMs);
    const startDeltaPx = Math.abs(snappedStart - clampedStart) * pxPerMs;
    const endDeltaPx = Math.abs(snappedEnd - (clampedStart + barDurationMs)) * pxPerMs;
    if (startDeltaPx <= EDGE_SNAP_PX && startDeltaPx <= endDeltaPx) {
      return snappedStart;
    }
    if (endDeltaPx <= EDGE_SNAP_PX) {
      return Math.max(0, snappedEnd - barDurationMs);
    }
    return clampedStart;
  }

  function snappedResizeDurationMs(nodeId: string, durationMs: number, pxPerMs = pixelsPerMs): number {
    const bar = motionBars.find(candidate => candidate.timing.motion.id === nodeId);
    if (!bar) {
      return Math.max(MIN_DURATION_MS, durationMs);
    }
    const endMs = bar.startMs + Math.max(MIN_DURATION_MS, durationMs) + bar.stutterTailMs;
    const snappedEndMs = snapTimeToBarEdges(endMs, nodeId, pxPerMs);
    return Math.max(MIN_DURATION_MS, snappedEndMs - bar.startMs - bar.stutterTailMs);
  }

  function snapTimeToBarEdges(timeMs: number, excludeNodeId: string, pxPerMs: number): number {
    let best = timeMs;
    let bestDistancePx = EDGE_SNAP_PX + 0.0001;
    for (const bar of motionBars.map(bar => ({ nodeId: bar.timing.motion.id, startMs: bar.startMs, endMs: bar.endMs }))) {
      if (bar.nodeId === excludeNodeId || bar.nodeId.startsWith(`${excludeNodeId}:`)) continue;
      for (const edgeMs of [bar.startMs, bar.endMs]) {
        const distancePx = Math.abs(edgeMs - timeMs) * pxPerMs;
        if (distancePx < bestDistancePx) {
          best = edgeMs;
          bestDistancePx = distancePx;
        }
      }
    }
    return best;
  }

  useEffect(() => {
    if (!editingName) return;
    requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
  }, [editingName?.nodeId]);

  useEffect(() => {
    setVerticalPanPx(current => clampVerticalPanPx(current));
  }, [laneCount]);

  useEffect(() => {
    setPanOffsetMs(current => clampHorizontalPanMs(current));
  }, [pixelsPerMs, timelineContentEndMs]);

  useEffect(() => {
    const wasPlaying = previousPlayingRef.current;
    previousPlayingRef.current = playing;
    if (playing && !wasPlaying) {
      revealTime(timeMs);
    }
  }, [playing, timeMs]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (drag.kind === "none") return;
      e.preventDefault();

      const dx = e.clientX - drag.startX;

      if (drag.kind === "scrub") {
        onTimeChange(Math.max(0, drag.startTimeMs + dx / drag.pxPerMs));
      } else if (drag.kind === "moveNode") {
        const startMs = snappedMoveStartMs(drag.nodeId, drag.startStartMs + dx / drag.pxPerMs, drag.startDurationMs, drag.pxPerMs);
        setDragPreview({ nodeId: drag.nodeId, offsetMs: startMs });
      } else if (drag.kind === "resizeNode") {
        const durationMs = snappedResizeDurationMs(drag.nodeId, drag.startDurationMs + dx / drag.pxPerMs, drag.pxPerMs);
        setDragPreview({ nodeId: drag.nodeId, durationMs });
      } else if (drag.kind === "moveLoop") {
        const widthMs = drag.startRegion.endMs - drag.startRegion.startMs;
        const unclampedStart = drag.startRegion.startMs + dx / drag.pxPerMs;
        const startMs = Math.max(0, Math.min(unclampedStart, Math.max(0, durationMs - widthMs)));
        onLoopRegionChange({ startMs: Math.round(startMs), endMs: Math.round(startMs + widthMs) });
      } else if (drag.kind === "resizeLoop") {
        const deltaMs = dx / drag.pxPerMs;
        const nextRegion = drag.edge === "start"
          ? { startMs: drag.startRegion.startMs + deltaMs, endMs: drag.startRegion.endMs }
          : { startMs: drag.startRegion.startMs, endMs: drag.startRegion.endMs + deltaMs };
        onLoopRegionChange(resizeLoopRegion(nextRegion, drag.edge, durationMs));
      } else if (drag.kind === "pan") {
        const dy = e.clientY - drag.startY;
        updateViewport(current => ({
          ...current,
          panOffsetMs: clampHorizontalPanMs(drag.startPanMs - dx / drag.pxPerMs, drag.pxPerMs),
          verticalPanPx: clampVerticalPanPx(drag.startVerticalPanPx - dy),
        }));
      }
    }

    function onMouseUp(e: MouseEvent) {
      const drag = dragRef.current;
      if (drag.kind === "none") return;
      const releaseFocusOnEnd = drag.kind === "scrub" && drag.releaseFocusOnEnd;

      const dx = e.clientX - drag.startX;

      if (drag.kind === "moveNode") {
        const startMs = snappedMoveStartMs(drag.nodeId, drag.startStartMs + dx / drag.pxPerMs, drag.startDurationMs, drag.pxPerMs);
        const nextOffsetMs = Math.round(startMs - drag.offsetBaseMs);
        const currentOffsetMs = Math.round(drag.startStartMs - drag.offsetBaseMs);
        if (nextOffsetMs !== currentOffsetMs) {
          onUpdateMotionNode(drag.nodeId, { offsetMs: nextOffsetMs });
        }
      } else if (drag.kind === "resizeNode") {
        const nextDurationMs = Math.round(snappedResizeDurationMs(drag.nodeId, drag.startDurationMs + dx / drag.pxPerMs, drag.pxPerMs));
        if (nextDurationMs !== Math.round(drag.startDurationMs)) {
          onUpdateMotionNode(drag.nodeId, { durationMs: nextDurationMs });
        }
      }

      setDragPreview(null);
      dragRef.current = { kind: "none" };
      if (releaseFocusOnEnd && timelineRef.current === globalThis.document.activeElement) {
        timelineRef.current?.blur();
      }
    }

    globalThis.document.addEventListener("mousemove", onMouseMove);
    globalThis.document.addEventListener("mouseup", onMouseUp);
    return () => {
      globalThis.document.removeEventListener("mousemove", onMouseMove);
      globalThis.document.removeEventListener("mouseup", onMouseUp);
    };
  }, [durationMs, laneCount, motionBars, onLoopRegionChange, onTimeChange, onUpdateMotionNode]);

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    if (editingName) return;
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const releaseFocusAfterScrub = shouldReleaseTimelineFocusAfterPointerScrub(globalThis.document.activeElement === timelineRef.current);
    timelineRef.current?.focus();

    // Ruler zone scrubs the playhead.
    if (y <= RULER_HEIGHT) {
      if (effectiveLoopRegion && y <= LOOP_HEIGHT + 3) {
        const loopX = timeToX(effectiveLoopRegion.startMs);
        const loopW = Math.max(4, (effectiveLoopRegion.endMs - effectiveLoopRegion.startMs) * pixelsPerMs);
        if (x >= loopX && x <= loopX + loopW) {
          if (x <= loopX + RESIZE_PX) {
            dragRef.current = { kind: "resizeLoop", edge: "start", startX: e.clientX, startRegion: effectiveLoopRegion, pxPerMs: pixelsPerMs };
          } else if (x >= loopX + loopW - RESIZE_PX) {
            dragRef.current = { kind: "resizeLoop", edge: "end", startX: e.clientX, startRegion: effectiveLoopRegion, pxPerMs: pixelsPerMs };
          } else {
            dragRef.current = { kind: "moveLoop", startX: e.clientX, startRegion: effectiveLoopRegion, pxPerMs: pixelsPerMs };
          }
          e.preventDefault();
          return;
        }
      }

      const t = Math.max(0, xToTime(x));
      onTimeChange(t);
      dragRef.current = { kind: "scrub", startX: e.clientX, startTimeMs: t, pxPerMs: pixelsPerMs, releaseFocusOnEnd: releaseFocusAfterScrub };
      e.preventDefault();
      return;
    }

    // Playhead handle click (within 5px)
    const phX = timeToX(timeMs);
    if (Math.abs(x - phX) <= 5) {
      dragRef.current = { kind: "scrub", startX: e.clientX, startTimeMs: timeMs, pxPerMs: pixelsPerMs, releaseFocusOnEnd: releaseFocusAfterScrub };
      e.preventDefault();
      return;
    }

    const bodyY = y - RULER_HEIGHT + verticalPanPx;
    const laneIndex = Math.floor(bodyY / ROW_SLOT);
    const hitBar = [...motionBars]
      .reverse()
      .find(bar => {
        if (bar.lane !== laneIndex) return false;
        const nodeX = timelineBarHitLeft(bar, timeToX(bar.startMs));
        const nodeW = timelineBarHitWidth(bar, pixelsPerMs);
        return x >= nodeX && x <= nodeX + nodeW;
      });

    if (hitBar) {
      const node = hitBar.timing.motion;
      const nodeX = timelineBarHitLeft(hitBar, timeToX(hitBar.startMs));
      const nodeW = timelineBarHitWidth(hitBar, pixelsPerMs);
      const editableW = hitBar.isMarker ? nodeW : Math.max(4, hitBar.durationMs * pixelsPerMs);
      if (e.detail >= 2 && (hitBar.isMarker || x < nodeX + editableW - RESIZE_PX || x > nodeX + editableW)) {
        startRename(node);
        e.preventDefault();
        return;
      }
      if (!hitBar.isMarker && x >= nodeX + editableW - RESIZE_PX && x <= nodeX + editableW) {
        onSelectAnimationNodes([node.id], node.id, { switchRoot: e.metaKey || e.ctrlKey });
        dragRef.current = { kind: "resizeNode", nodeId: node.id, startX: e.clientX, startDurationMs: hitBar.durationMs, pxPerMs: pixelsPerMs };
      } else {
        onSelectAnimationNodes([node.id], node.id, { switchRoot: e.metaKey || e.ctrlKey });
        dragRef.current = { kind: "moveNode", nodeId: node.id, startX: e.clientX, startStartMs: hitBar.startMs, startDurationMs: hitBar.effectiveDurationMs, offsetBaseMs: hitBar.offsetBaseMs, pxPerMs: pixelsPerMs };
      }
      e.preventDefault();
      return;
    }

    // Empty area deselects and pans.
    onSelectAnimationNodes([], undefined);
    dragRef.current = { kind: "pan", startX: e.clientX, startY: e.clientY, startPanMs: panOffsetMs, startVerticalPanPx: verticalPanPx, pxPerMs: pixelsPerMs };
    e.preventDefault();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (editingName) return;
    if (e.key.toLowerCase() !== "l") return;
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return;
    e.preventDefault();
    if (effectiveLoopRegion) {
      onLoopRegionChange(undefined);
      return;
    }
    onLoopRegionChange(selectedLoopBounds());
  }

  function startRename(node: MotionAnimationNode): void {
    dragRef.current = { kind: "none" };
    onSelectAnimationNodes([node.id], node.id);
    setDragPreview(null);
    setEditingName({ nodeId: node.id, value: node.name });
  }

  function commitRename(): void {
    if (!editingName) return;
    const node = animationGraph.nodes.find(candidate => candidate.id === editingName.nodeId);
    if (!node || node.kind !== "motion") {
      setEditingName(null);
      return;
    }
    const nextName = editingName.value.trim();
    if (nextName.length > 0 && nextName !== node.name) {
      onUpdateMotionNode(node.id, { name: nextName });
    }
    setEditingName(null);
  }

  function handleWheel(e: React.WheelEvent) {
    e.preventDefault();
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const deltaX = normalizeWheelDelta(e.deltaX, e.deltaMode);
    const deltaY = normalizeWheelDelta(e.deltaY, e.deltaMode);
    const shouldZoom = e.ctrlKey || e.metaKey || e.altKey;

    if (shouldZoom) {
      const pivotTime = xToTime(x);
      const factor = Math.exp(-deltaY * 0.003);
      const newPxPerMs = Math.min(MAX_PIXELS_PER_MS, Math.max(MIN_PIXELS_PER_MS, pixelsPerMs * factor));
      const newPan = clampHorizontalPanMs(pivotTime - x / newPxPerMs, newPxPerMs);
      updateViewport(current => ({
        ...current,
        pixelsPerMs: newPxPerMs,
        panOffsetMs: newPan,
      }));
      return;
    }

    updateViewport(current => ({
      ...current,
      panOffsetMs: clampHorizontalPanMs(current.panOffsetMs + deltaX / pixelsPerMs),
      verticalPanPx: clampVerticalPanPx(current.verticalPanPx + deltaY),
    }));
  }

  // Ruler ticks
  const tickStep = niceTickStep(pixelsPerMs);
  const visibleMs = (timelineRef.current?.clientWidth ?? 800) / pixelsPerMs;
  const firstTick = Math.max(0, Math.ceil(panOffsetMs / tickStep) * tickStep - tickStep);
  const ticks: number[] = [];
  for (let t = firstTick; t <= panOffsetMs + visibleMs + tickStep; t += tickStep) {
    ticks.push(t);
    if (ticks.length > 200) break;
  }

  const playheadX = timeToX(timeMs);
  const isDragging = dragRef.current.kind !== "none";

  return (
    <div className="flex h-full bg-[#0d1017]">
      {showTrackLabels ? (
        <div
          className="relative shrink-0 overflow-hidden border-r border-white/10 bg-[#10151d]"
          style={{ width: TRACK_LABEL_WIDTH }}
        >
          <div
            className="absolute left-0 right-0 top-0 border-b border-white/10 bg-[#0d1017]"
            style={{ height: RULER_HEIGHT }}
          />
          <div className="absolute left-0 right-0 overflow-hidden" style={{ top: RULER_HEIGHT, bottom: 0 }}>
            {timelineTracks.map(track => (
              <div
                key={track.rootNodeId}
                className="absolute left-0 right-0 flex items-start border-b border-white/[0.06] px-2 py-1.5"
                style={{
                  top: track.laneStart * ROW_SLOT - verticalPanPx,
                  height: track.laneCount * ROW_SLOT,
                }}
              >
                <div className="min-w-0 truncate text-[10px] font-medium text-slate-400" title={track.name}>
                  {track.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {/* Timeline area */}
      <div
        ref={timelineRef}
        className="relative flex-1 overflow-hidden outline-none"
        style={{ cursor: isDragging ? "grabbing" : "default" }}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
      >
        {/* Ruler */}
        <div
          className="absolute left-0 right-0 top-0 bg-[#0d1017] border-b border-white/10 overflow-hidden"
          style={{ height: RULER_HEIGHT }}
        >
          {effectiveLoopRegion ? (
            <div
              className="absolute top-0 z-10 rounded-sm border border-amber-300/80 bg-amber-400/45 shadow-[0_0_0_1px_rgba(245,158,11,0.18)]"
              style={{
                left: timeToX(effectiveLoopRegion.startMs),
                width: Math.max(4, (effectiveLoopRegion.endMs - effectiveLoopRegion.startMs) * pixelsPerMs),
                height: LOOP_HEIGHT,
              }}
            >
              <div className="absolute left-0 top-0 h-full w-1 cursor-ew-resize bg-amber-200/80" />
              <div className="absolute right-0 top-0 h-full w-1 cursor-ew-resize bg-amber-200/80" />
            </div>
          ) : null}
          {ticks.map(tick => {
            const x = timeToX(tick);
            if (x < -60) return null;
            return (
              <div key={tick} className="absolute top-0 bottom-0" style={{ left: x }}>
                <div className="absolute bottom-0 w-px bg-white/15" style={{ height: 5 }} />
                <div className="absolute text-[9px] text-slate-500 whitespace-nowrap" style={{ top: 3, left: 2 }}>
                  {formatMs(tick)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Node rows */}
        <div className="absolute left-0 right-0 overflow-hidden" style={{ top: RULER_HEIGHT, bottom: 0 }}>
          {showTrackLabels ? timelineTracks.map((track, index) => (
            <div
              key={`${track.rootNodeId}:track-bg`}
              className={`pointer-events-none absolute left-0 right-0 border-b border-white/[0.06] ${index % 2 === 0 ? "bg-white/[0.015]" : ""}`}
              style={{
                top: track.laneStart * ROW_SLOT - verticalPanPx,
                height: track.laneCount * ROW_SLOT,
              }}
            />
          )) : null}
          {motionBars.map(bar => {
            const node = bar.timing.motion;
            const selected = selectedAnimationNodeIds.includes(node.id);
            const transition = Boolean(node.transition);
            const x = timeToX(bar.startMs);
            const y = bar.lane * ROW_SLOT - verticalPanPx;
            const w = Math.max(4, bar.durationMs * pixelsPerMs);
            const tailW = Math.max(0, bar.stutterTailMs * pixelsPerMs);

            return (
              <div
                key={node.id}
                className="absolute left-0 right-0"
                style={{ top: y, height: ROW_SLOT }}
              >
                {bar.isMarker ? (
                  <div
                    className="absolute flex items-center"
                    style={{ left: x - MARKER_LINE_WIDTH / 2, width: MARKER_LABEL_WIDTH, top: BAR_TOP, height: BAR_HEIGHT }}
                    onDoubleClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      startRename(node);
                    }}
                  >
                    <div className="grid h-full shrink-0 place-items-center" style={{ width: MARKER_LINE_WIDTH }}>
                      <div className={`h-full w-0.5 rounded-full ${selected ? transition ? "bg-orange-300" : "bg-sky-300" : transition ? "bg-orange-400/75" : "bg-emerald-400/75"}`} />
                    </div>
                    <div
                      className={`ml-1 flex h-[18px] min-w-0 flex-1 items-center rounded-sm border px-1.5 ${
                        selected
                          ? transition ? "border-orange-300/55 bg-orange-500/20" : "border-sky-400/55 bg-sky-500/20"
                          : transition ? "border-orange-500/35 bg-orange-500/10 hover:border-orange-300/50" : "border-emerald-500/30 bg-emerald-500/10 hover:border-emerald-300/45"
                      }`}
                    >
                      {editingName?.nodeId === node.id ? (
                        <input
                          ref={renameInputRef}
                          value={editingName.value}
                          onChange={event => setEditingName({ nodeId: node.id, value: event.currentTarget.value })}
                          onBlur={commitRename}
                          onKeyDown={event => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitRename();
                            } else if (event.key === "Escape") {
                              event.preventDefault();
                              setEditingName(null);
                            }
                          }}
                          onMouseDown={event => event.stopPropagation()}
                          onDoubleClick={event => event.stopPropagation()}
                          className="min-w-0 flex-1 border-0 bg-black/25 px-1 py-0 text-[10px] text-sky-100 outline-none"
                        />
                      ) : (
                        <span className={`pointer-events-none min-w-0 flex-1 truncate text-[10px] ${selected ? transition ? "text-orange-100" : "text-sky-200" : "text-slate-300"}`}>
                          {node.name}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="absolute" style={{ left: x, width: Math.max(w + tailW, w), top: BAR_TOP, height: BAR_HEIGHT }}>
                    {tailW > 0 ? (
                      <div
                        className={`absolute top-0 h-full rounded-r-sm border border-l-0 border-dashed ${
                          selected
                            ? transition ? "border-orange-300/45 bg-orange-400/10" : "border-sky-300/40 bg-sky-400/10"
                            : transition ? "border-orange-500/35 bg-orange-500/[0.08]" : "border-cyan-400/25 bg-cyan-400/[0.08]"
                        }`}
                        style={{ left: w, width: tailW }}
                      />
                    ) : null}
                    <div
                      className={`absolute flex items-center overflow-hidden rounded-sm ${
                        selected
                          ? transition ? "border border-dashed border-orange-300/70 bg-orange-500/20" : "bg-sky-500/20 border border-sky-400/60"
                          : transition ? "border border-dashed border-orange-500/60 bg-orange-500/15 hover:border-orange-300/60" : "bg-[#1c3858] border border-[#2a4f78] hover:border-sky-700/50"
                      }`}
                      style={{ left: 0, width: w, top: 0, height: BAR_HEIGHT }}
                      onDoubleClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        startRename(node);
                      }}
                    >
                      {editingName?.nodeId === node.id ? (
                        <input
                          ref={renameInputRef}
                          value={editingName.value}
                          onChange={event => setEditingName({ nodeId: node.id, value: event.currentTarget.value })}
                          onBlur={commitRename}
                          onKeyDown={event => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitRename();
                            } else if (event.key === "Escape") {
                              event.preventDefault();
                              setEditingName(null);
                            }
                          }}
                          onMouseDown={event => event.stopPropagation()}
                          onDoubleClick={event => event.stopPropagation()}
                          className="mx-1 min-w-0 flex-1 border-0 bg-black/25 px-1 py-0 text-[10px] text-sky-100 outline-none"
                        />
                      ) : (
                        <span className={`px-1.5 text-[10px] truncate pointer-events-none flex-1 min-w-0 ${selected ? transition ? "text-orange-100" : "text-sky-200" : "text-slate-300"}`}>
                          {node.name}
                        </span>
                      )}
                      {/* Resize grip */}
                      <div
                        className="shrink-0 flex items-center justify-center h-full"
                        style={{ width: RESIZE_PX, cursor: "ew-resize" }}
                      >
                        <div className={`w-px h-3 ${selected ? transition ? "bg-orange-300/60" : "bg-sky-400/50" : "bg-slate-500/40"}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {motionBars.length === 0 && (
            <div className="px-2.5 pt-2.5 text-[10px] text-slate-600 select-none">
              {rootNodeId ? "No Motion nodes" : "No animation selected"}
            </div>
          )}
        </div>

        {/* Playhead */}
        {playheadX > -8 && (
          <div
            className="absolute top-0 bottom-0 z-10 pointer-events-none"
            style={{ left: playheadX }}
          >
            <div className="absolute inset-y-0 w-px bg-sky-400/80" />
            <svg
              className="absolute"
              style={{ top: 7, left: -4, width: 8, height: 10 }}
              viewBox="0 0 8 10"
            >
              <polygon points="4,0 8,5 4,10 0,5" fill="rgb(56 189 248 / 0.9)" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

function normalizeWheelDelta(delta: number, mode: number): number {
  if (mode === 1) return delta * 16;
  if (mode === 2) return delta * 800;
  return delta;
}

function normalizeLoopRegion(region: AnimationLoopRegion | undefined, durationMs: number): AnimationLoopRegion | undefined {
  if (!region || durationMs <= 0) return undefined;
  const startMs = Math.max(0, Math.min(region.startMs, durationMs));
  const endMs = Math.max(0, Math.min(region.endMs, durationMs));
  if (endMs - startMs < MIN_LOOP_DURATION_MS) return undefined;
  return { startMs: Math.round(startMs), endMs: Math.round(endMs) };
}

function timelineMotionTimings(graph: AnimationGraph, rootNodeId: string | undefined, document: CurvaDocument): TimelineMotionTiming[] {
  if (!rootNodeId) {
    return [];
  }

  const timings: TimelineMotionTiming[] = [];
  const visit = (currentRootNodeId: string, baseOffsetMs: number, rootDepth: number, seenRootIds: Set<string>) => {
    if (seenRootIds.has(currentRootNodeId)) {
      return;
    }
    const nextSeenRootIds = new Set(seenRootIds).add(currentRootNodeId);
    for (const timing of animationMotionTimings(graph, currentRootNodeId, document)) {
      timings.push({
        timing: {
          ...timing,
          startMs: baseOffsetMs + timing.startMs,
          endMs: baseOffsetMs + timing.endMs,
          offsetBaseMs: baseOffsetMs + timing.offsetBaseMs,
        },
        rootNodeId: currentRootNodeId,
        offsetBaseMs: baseOffsetMs + timing.offsetBaseMs,
        rootDepth,
      });
    }

    for (const transition of animationTransitionTimings(graph, currentRootNodeId, document)) {
      if (nextSeenRootIds.has(transition.targetAnimationNodeId)) {
        continue;
      }
      visit(transition.targetAnimationNodeId, baseOffsetMs + transition.timing.endMs, rootDepth + 1, nextSeenRootIds);
    }
  };

  visit(rootNodeId, 0, 0, new Set());
  return timings.sort((left, right) => (
    left.timing.startMs - right.timing.startMs ||
    left.rootDepth - right.rootDepth ||
    left.timing.motion.id.localeCompare(right.timing.motion.id)
  ));
}

function resizeLoopRegion(region: AnimationLoopRegion, edge: "start" | "end", durationMs: number): AnimationLoopRegion {
  if (edge === "start") {
    const startMs = Math.max(0, Math.min(region.startMs, region.endMs - MIN_LOOP_DURATION_MS));
    return { startMs: Math.round(startMs), endMs: Math.round(Math.min(durationMs, region.endMs)) };
  }
  const endMs = Math.min(durationMs, Math.max(region.endMs, region.startMs + MIN_LOOP_DURATION_MS));
  return { startMs: Math.round(Math.max(0, region.startMs)), endMs: Math.round(endMs) };
}

function packTimelineTracks(bars: TimelineBar[], graph: AnimationGraph): { bars: TimelineBar[]; tracks: TimelineTrack[] } {
  const rootOrder: string[] = [];
  const barsByRootId = new Map<string, TimelineBar[]>();
  for (const bar of bars) {
    if (!barsByRootId.has(bar.rootNodeId)) {
      barsByRootId.set(bar.rootNodeId, []);
      rootOrder.push(bar.rootNodeId);
    }
    barsByRootId.get(bar.rootNodeId)!.push(bar);
  }

  const tracks: TimelineTrack[] = [];
  const packedBars: TimelineBar[] = [];
  let laneStart = 0;
  for (const rootNodeId of rootOrder) {
    const rootBars = barsByRootId.get(rootNodeId) ?? [];
    const packedRootBars = packTimelineBars(rootBars);
    const localLaneCount = Math.max(1, packedRootBars.reduce((count, bar) => Math.max(count, bar.lane + 1), 0));
    const rootNode = graph.nodes.find(node => node.id === rootNodeId);
    tracks.push({
      rootNodeId,
      name: rootNode?.kind === "animation" ? rootNode.name : "Animation",
      laneStart,
      laneCount: localLaneCount,
    });
    packedBars.push(...packedRootBars.map(bar => ({ ...bar, lane: laneStart + bar.lane })));
    laneStart += localLaneCount;
  }

  return { bars: packedBars, tracks };
}

function packTimelineBars(bars: TimelineBar[]): TimelineBar[] {
  const timedBars = bars.filter(bar => !bar.isMarker);
  const markerBars = bars.filter(bar => bar.isMarker);
  const packedTimedBars = packTimedTimelineBars(timedBars);
  const timedLaneCount = packedTimedBars.reduce((count, bar) => Math.max(count, bar.lane + 1), 0);
  const markerLane = Math.max(0, timedLaneCount);
  const markerLanesByMotionId = new Map(markerBars.map(bar => [bar.timing.motion.id, markerLane] as const));
  const timedLanesByMotionId = new Map(packedTimedBars.map(bar => [bar.timing.motion.id, bar.lane] as const));

  return bars.map(bar => ({
    ...bar,
    lane: bar.isMarker
      ? markerLanesByMotionId.get(bar.timing.motion.id) ?? markerLane
      : timedLanesByMotionId.get(bar.timing.motion.id) ?? 0,
  }));
}

function packTimedTimelineBars(bars: TimelineBar[]): TimelineBar[] {
  const laneEnds: number[] = [];
  const sorted = [...bars].sort((left, right) => (
    left.startMs - right.startMs ||
    left.packEndMs - right.packEndMs ||
    left.originalIndex - right.originalIndex
  ));
  const lanesByMotionId = new Map<string, number>();

  for (const bar of sorted) {
    const minLane = bar.hasOffset ? 1 : 0;
    let lane = laneEnds.findIndex((endMs, index) => index >= minLane && endMs <= bar.startMs + 0.0001);
    if (lane === -1) {
      lane = Math.max(minLane, laneEnds.length);
      while (laneEnds.length < lane) {
        laneEnds.push(0);
      }
      laneEnds[lane] = bar.packEndMs;
    } else {
      laneEnds[lane] = bar.packEndMs;
    }
    lanesByMotionId.set(bar.timing.motion.id, lane);
  }

  return bars.map(bar => ({
    ...bar,
    lane: lanesByMotionId.get(bar.timing.motion.id) ?? 0,
  }));
}

function timelineBarHitWidth(bar: TimelineBar, pixelsPerMs: number): number {
  return bar.isMarker ? MARKER_LABEL_WIDTH : Math.max(4, bar.effectiveDurationMs * pixelsPerMs);
}

function timelineBarHitLeft(bar: TimelineBar, x: number): number {
  return bar.isMarker ? x - MARKER_LINE_WIDTH / 2 : x;
}
